import { useState, useRef, useEffect } from 'react';
import {
  X,
  Flag,
  Check,
  WarningIcon,
  SparkleIcon,
  ArrowDown,
  XCircleIcon,
  CheckCircleIcon,
} from '@phosphor-icons/react';
import axios from 'axios';
import Latex from '../../../../apps/frontend/src/components/global/Latex';
import ReportModal from '../components/modals/ReportModal';
import moment from 'moment';
import TorchGuide from '../components/generic/TorchGuide';

const QuestionAttempt = ({
  data,
  onClose,
  packId,
  testId,
  onQuestionAttempted,
  onAllQuestionsAttempted,
  attemptedQuestions,
  checkSimilarQuestionsButton,
}) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [numericalInput, setNumericalInput] = useState('');
  const [isAnswered, setIsAnswered] = useState(false);
  const [showSolution, setShowSolution] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [showReportModal, setShowReportModal] = useState(false);
  const [isReporting, setIsReporting] = useState(false);
  const commentsRef = useRef(null);
  const solutionRef = useRef(null);
  const checkAnswerButtonRef = useRef(null);
  const viewSolutionButtonRef = useRef(null);

  const [user, setUser] = useState(JSON.parse(localStorage.getItem('userProfile')));
  const [showTorchGuide, setShowTorchGuide] = useState(true);
  const [stepIndex, setStepIndex] = useState(0);

  // Torch guide steps
  const steps = [
    {
      target: '.check-answer-button-target',
      content: 'Click here to check the correct answer',
      title: 'Check Answer',
      disableBeacon: true,
      spotlightPadding: 0,
    },
    {
      target: '.view-solution-button-target',
      content: 'Click here to view the solution for this question',
      title: 'View Solution',
      disableBeacon: true,
      spotlightPadding: 0,
    },
  ];

  const QUESTION_REPORT_LIMIT = 10;
  const DAY_LIMIT = 7;

  // Check if user has completed the tour
  useEffect(() => {
    const checkIfTablet = () => {
      setShowTorchGuide(window.innerWidth > 1024);
    };

    checkIfTablet();
    window.addEventListener('resize', checkIfTablet);
    return () => window.removeEventListener('resize', checkIfTablet);
  }, []);

  const handleJoyrideCallback = async (data) => {
    const { status, index, type, action } = data;

    if (status === 'finished') {
      setStepIndex(0);
      try {
        const res = await axios.patch('/api/user/profile', {
          attemptModalCheckAnswerTourDone: true,
          attemptModalViewSolutionTourDone: true,
        });

        const updatedUser = {
          ...user,
          attemptModalCheckAnswerTourDone: true,
          attemptModalViewSolutionTourDone: true,
        };
        localStorage.setItem('userProfile', JSON.stringify(updatedUser));
        setUser(updatedUser);
      } catch (error) {}
    } else if (type === 'step:after') {
      if (action === 'next') {
        setStepIndex(index + 1);
      } else if (action === 'prev') {
        setStepIndex(Math.max(0, index - 1));
      }
    }
  };

  // Report states exactly like in Solutions
  const [reportMessage, setReportMessage] = useState({
    success: true,
    daysLeft: DAY_LIMIT,
    count: QUESTION_REPORT_LIMIT,
  });
  const [reportStat, setReportStat] = useState({
    lastReported: null,
    currentReportCount: 0,
  });
  const [isReported, setIsReported] = useState(false);

  // Report options configuration
  const [reportOptions, setReportOptions] = useState([
    { checked: false, text: 'Error in question/option(s)/solution' },
    { checked: false, text: 'Missing/Wrong/Bad quality image(s)' },
    { checked: false, text: 'Wrong Answer Key' },
    { checked: false, text: 'Incorrect Solution' },
    { checked: false, text: 'Repeated Question' },
  ]);

  // Current question data
  const currentQuestionData = data.questions?.[currentQuestion] || data;
  const isNumerical = currentQuestionData.type === 'numerical';

  // Track that user has viewed similar questions
  const trackSimilarQuestionsViewed = async () => {
    try {
      const originalQuestionId = data?.questions?.[0]?.originalQuestionId;
      await axios.post(`/api/v1/similar-question/${originalQuestionId}/track`);
    } catch (error) {}
  };

  useEffect(() => {
    trackSimilarQuestionsViewed();
  }, []);

  // Reset states when question changes
  useEffect(() => {
    if (isNumerical) {
      setNumericalInput('');
    }
    // Reset report states when question changes
    setIsReported(false);
    setShowReportModal(false);
    setReportMessage({
      success: true,
      daysLeft: DAY_LIMIT,
      count: QUESTION_REPORT_LIMIT,
    });
    // Reset report options
    setReportOptions([
      { checked: false, text: 'Error in question/option(s)/solution' },
      { checked: false, text: 'Missing/Wrong/Bad quality image(s)' },
      { checked: false, text: 'Wrong Answer Key' },
      { checked: false, text: 'Incorrect Solution' },
      { checked: false, text: 'Repeated Question' },
    ]);
  }, [currentQuestion, isNumerical]);

  // Scroll to solution when showSolution changes
  useEffect(() => {
    if (showSolution && solutionRef.current) {
      solutionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showSolution]);

  // Check if question is already reported
  const isQuestionAlreadyReported = () => {
    const questions = JSON.parse(localStorage.getItem('reportedQuestions')) || [];
    return questions.includes(currentQuestionData._id);
  };

  // Check question report limit exactly like in Solutions
  const checkQuestionReportLimit = () => {
    const rstat = JSON.parse(localStorage.getItem('reportStat'));
    if (!rstat) {
      setReportMessage({
        success: true,
        daysLeft: DAY_LIMIT,
        count: QUESTION_REPORT_LIMIT,
      });
      return 1;
    }

    const current = moment(new Date());
    const start = moment(rstat.lastReported);
    const daysPassed = current.diff(start, 'days');

    if (rstat.currentReportCount >= QUESTION_REPORT_LIMIT) {
      if (daysPassed < DAY_LIMIT) {
        setReportMessage({
          success: false,
          message: `You have reached report limit. Please wait ${DAY_LIMIT - daysPassed} days to report more.`,
          daysLeft: DAY_LIMIT - daysPassed,
          count: QUESTION_REPORT_LIMIT - rstat.currentReportCount,
        });
        return 0;
      } else {
        // Reset stats since limit window passed
        const resetStat = {
          lastReported: new Date(),
          currentReportCount: 0,
        };
        localStorage.setItem('reportStat', JSON.stringify(resetStat));
        setReportMessage({
          success: true,
          daysLeft: DAY_LIMIT,
          count: QUESTION_REPORT_LIMIT,
        });
        return 1;
      }
    } else {
      setReportMessage({
        success: true,
        daysLeft: DAY_LIMIT - daysPassed,
        count: QUESTION_REPORT_LIMIT - rstat.currentReportCount,
      });
      return 1;
    }
  };

  // Scroll to solution
  const scrollToSolution = () => {
    if (!showSolution) {
      setShowSolution(true);
    } else if (solutionRef.current) {
      solutionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // Option selection
  const handleOptionSelect = (index, e) => {
    e.stopPropagation();
    if (!isAnswered) {
      setSelectedOption(index);
    }
  };

  // Numerical input handler
  const handleNumericalInput = (value) => {
    if (!isAnswered) {
      const sanitizedValue = value.replace(/[^0-9.]/g, '');
      setNumericalInput(sanitizedValue);

      if (sanitizedValue) {
        setSelectedOption(0);
      } else {
        setSelectedOption(null);
      }
    }
  };

  // Check answer
  const handleCheckAnswer = async () => {
    if (isNumerical && !numericalInput) {
      return;
    }

    if (!isNumerical && selectedOption === null) {
      return;
    }

    if ((!isNumerical && selectedOption !== null) || (isNumerical && numericalInput)) {
      setIsAnswered(true);

      try {
        const currentQ = data.questions[currentQuestion];
        const apiUrl = `/api/v1/similar-question/${currentQ._id}/attempt`;

        const payload = {
          markedOptions:
            currentQ.type === 'singleCorrect' || currentQ.type === 'multipleCorrect'
              ? [currentQ.options[selectedOption].id]
              : [],
          inputValue: currentQ.type === 'numerical' ? numericalInput : null,
          packId,
          testId,
        };

        const response = await axios.post(apiUrl, payload, { timeout: 10000 });
        onQuestionAttempted(currentQ._id);

        if (currentQuestion === data.questions.length - 1) {
          onAllQuestionsAttempted();
        }
      } catch (error) {
        if (currentQuestion === data.questions.length - 1) {
          onAllQuestionsAttempted();
        }
      }
    }
  };

  // Check if all questions attempted
  const checkIfAllQuestionsAttempted = () => {
    if (attemptedQuestions && data.questions) {
      return attemptedQuestions.length >= data.questions.length;
    }
    return false;
  };

  // Next question
  const handleNext = () => {
    if (currentQuestion < data.questions?.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedOption(null);
      setNumericalInput('');
      setIsAnswered(false);
      setShowSolution(false);
    } else {
      const allAttempted = checkIfAllQuestionsAttempted();
      if (allAttempted || isAnswered) {
        onAllQuestionsAttempted();
        onClose();
      } else {
        onAllQuestionsAttempted();
      }
    }
  };

  // Previous question
  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setSelectedOption(null);
      setNumericalInput('');
      setIsAnswered(false);
      setShowSolution(false);
    }
  };

  // Report option change
  const handleReportOptionChange = (value, index) => {
    const newReportOptions = [...reportOptions];
    newReportOptions[index].checked = value;
    setReportOptions(newReportOptions);
  };

  // Report question
  const handleReportQuestion = async () => {
    setIsReporting(true);

    try {
      const currentQ = data.questions[currentQuestion];

      // Check if question is already reported
      if (isQuestionAlreadyReported()) {
        checkQuestionReportLimit();
        setReportMessage((prev) => ({
          ...prev,
          success: false,
          message: 'You have already reported this question.',
        }));
        setIsReporting(false);
        return;
      }

      // Check report limits
      const limitCheck = checkQuestionReportLimit();
      if (!limitCheck) {
        setIsReporting(false);
        return;
      }

      const errorTypes = reportOptions.filter((opt) => opt.checked).map((opt) => opt.text);

      // API call
      await axios.post(`/api/v1/similar-question/${currentQ._id}/report`, {
        errorTypes,
        comments: commentsRef.current?.value || '',
        packId,
        testId,
        studentQuestionLink: window.location.href,
      });

      // Update localStorage
      let reportedQuestions = JSON.parse(localStorage.getItem('reportedQuestions')) || [];
      reportedQuestions.push(currentQ._id);
      localStorage.setItem('reportedQuestions', JSON.stringify(reportedQuestions));

      const rstat = JSON.parse(localStorage.getItem('reportStat')) || {
        lastReported: new Date(),
        currentReportCount: 0,
      };

      localStorage.setItem(
        'reportStat',
        JSON.stringify({
          ...rstat,
          lastReported: new Date(),
          currentReportCount: rstat.currentReportCount + 1,
        }),
      );

      setIsReported(true);

      // Update report message
      const updatedStat = JSON.parse(localStorage.getItem('reportStat'));
      const current = moment(new Date());
      const daysPassed = current.diff(moment(updatedStat.lastReported), 'days');

      setReportMessage({
        success: true,
        message: 'Question reported successfully',
        count: QUESTION_REPORT_LIMIT - updatedStat.currentReportCount,
        daysLeft: DAY_LIMIT - daysPassed,
      });
    } catch (error) {
      setReportMessage({
        success: false,
        message: 'Failed to report question',
        count: 0,
        daysLeft: 0,
      });
    } finally {
      setIsReporting(false);
    }
  };

  // Check report status
  useEffect(() => {
    if (currentQuestionData._id) {
      const alreadyReported = isQuestionAlreadyReported();
      setIsReported(alreadyReported);
      checkQuestionReportLimit();
    }
  }, [currentQuestionData._id]);

  // Close modal
  const handleCloseModal = async () => {
    const allAttempted = checkIfAllQuestionsAttempted();

    if (allAttempted) {
      try {
        const currentQ = data.questions[currentQuestion];
        if (currentQ?._id && checkSimilarQuestionsButton) {
          await checkSimilarQuestionsButton(currentQ._id);
        }
      } catch (err) {}
    }

    onClose();
  };

  // Render numerical input
  const renderNumericalInput = () => {
    const isCorrect =
      (currentQuestionData.numericalLowerLimit !== undefined &&
        currentQuestionData.numericalUpperLimit !== undefined &&
        numericalInput >= currentQuestionData.numericalLowerLimit &&
        numericalInput <= currentQuestionData.numericalUpperLimit) ||
      (currentQuestionData.correctValue !== undefined &&
        Number(numericalInput) === Number(currentQuestionData.correctValue));

    return (
      <div className="w-full flex flex-col md:flex-row gap-4 mb-4">
        {!isAnswered && (
          <div className="w-1/2 bg-[var(--q3-surface-dim)] rounded-lg">
            <div className="px-4 py-3 flex flex-col justify-center h-full">
              <div className="flex items-center justify-between px-2">
                <div className="flex flex-col gap-y-1 text-base font-medium text-[var(--q3-neutral-default)] w-full">
                  <input
                    type="number"
                    value={numericalInput}
                    onChange={(e) => handleNumericalInput(e.target.value)}
                    placeholder="Enter your answer"
                    className="w-full rounded-lg border bg-[var(--q3-surface-default)] px-3 py-2 text-base text-[var(--q3-neutral-default)] focus:outline-none focus:ring-2 focus:ring-[var(--q3-accent-normal)]"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {isAnswered && (
          <>
            <div className={`bg-[var(--q3-surface-dim)] rounded-lg ${isCorrect ? 'w-1/2' : 'flex-1'}`}>
              <div className="px-4 py-3 flex flex-col justify-center h-full">
                <div className="flex items-center justify-between">
                  <span className="text-base">{numericalInput}</span>
                  {isCorrect ? (
                    <span className="flex items-center gap-1 text-[var(--q3-green-normal)] text-sm font-medium">
                      Your Answer <CheckCircleIcon size={20} weight="fill" />
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-[var(--q3-danger-normal)] text-sm font-medium">
                      Your Answer <XCircleIcon size={20} weight="fill" />
                    </span>
                  )}
                </div>
              </div>
            </div>

            {!isCorrect && (
              <div className="flex-1 bg-[var(--q3-surface-dim)] rounded-lg">
                <div className="px-4 py-3 flex flex-col justify-center h-full">
                  <div className="flex items-center justify-between">
                    <span className="text-base">{currentQuestionData.correctValue}</span>
                    <span className="flex items-center gap-1 text-[var(--q3-green-normal)] text-sm font-medium">
                      Correct Answer <CheckCircleIcon size={20} weight="fill" />
                    </span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="fixed inset-0 flex items-center justify-center z-50 md:p-4">
        <div className="bg-[var(--q3-surface-default)] w-full h-full md:rounded-2xl md:w-full md:max-w-6xl md:h-[80vh] flex flex-col overflow-hidden">
          {/* Torch Guide */}
          {/* {!user?.attemptModalCheckAnswerTourDone && showTorchGuide && (
            <TorchGuide steps={steps} handleTorchGuideCallback={handleJoyrideCallback} stepIndex={stepIndex} />
          )} */}

          <div className="flex items-center justify-between p-4 md:p-6 border-b border-[var(--q3-surface-dim)] flex-shrink-0">
            <h3 className="text-lg font-medium bg-gradient-to-r from-[var(--q3-violet-normal)] to-[var(--q3-rose-normal)] bg-clip-text text-transparent">
              Attempt Similar Qs
            </h3>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowReportModal(true)}
                className="flex items-center gap-2 text-[var(--q3-danger-normal)] text-sm font-medium px-3 py-1 cursor-pointer"
                style={{
                  borderRadius: 'var(--radius-3xl, 360px)',
                  border: '1px solid var(--q3-stroke-normal)',
                  background: 'var(--q3-surface-default)',
                }}
              >
                <WarningIcon size={16} />
                Report
              </button>
              <button
                onClick={handleCloseModal}
                className="text-[var(--q3-neutral-default)] hover:text-[var(--q3-neutral-light)] p-1 cursor-pointer"
                style={{
                  borderRadius: 'var(--radius-3xl, 360px)',
                  border: '1px solid var(--q3-stroke-normal)',
                  background: 'var(--q3-surface-default)',
                }}
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            <div className="p-4 md:p-6">
              <div className="text-[var(--q3-neutral-light)] text-sm mb-4">
                Qs {currentQuestion + 1}/{data.questions?.length || 1}
              </div>

              <div className="mb-6">
                <p className="text-[var(--q3-neutral-default)] text-base leading-relaxed">
                  <Latex>{currentQuestionData.question?.text}</Latex>
                </p>
              </div>

              {isNumerical ? (
                renderNumericalInput()
              ) : (
                <div
                  className={`mb-6 
                    ${
                      currentQuestionData?.options?.every((opt) => opt.text.length <= 15)
                        ? 'space-y-3 md:space-y-0 md:grid md:grid-cols-2 md:gap-4'
                        : 'space-y-3'
                    }
                  `}
                >
                  {currentQuestionData.options?.map((option, index) => {
                    const isSelected = selectedOption === index;
                    const isCorrect = isAnswered && option.isCorrect;
                    const isWrong = isAnswered && isSelected && !option.isCorrect;
                    const isMarked = isSelected;

                    return (
                      <div className="w-full h-full" key={index}>
                        <div className="relative h-full">
                          <div
                            className={`h-full flex items-center rounded-[12px] border cursor-pointer ${
                              isCorrect
                                ? 'border-[var(--q3-green-normal)]'
                                : !isCorrect && isMarked && isAnswered
                                  ? 'border-[var(--q3-danger-normal)]'
                                  : 'border-[var(--q3-stroke-normal)] bg-[var(--q3-surface-default)]'
                            } ${isSelected && !isAnswered ? 'border-[var(--q3-accent-normal)] ' : ''}`}
                            onClick={(e) => handleOptionSelect(index, e)}
                          >
                            <div className="px-4 py-3 flex items-center w-full">
                              <div className="flex items-center w-full">
                                <div className="mr-4">
                                  <div
                                    className={`w-8 h-8 text-sm rounded-full flex items-center text-center justify-center ${
                                      isCorrect
                                        ? 'bg-[var(--q3-green-light)] text-[var(--q3-neutral-default)] '
                                        : !isCorrect && isMarked && isAnswered
                                          ? 'bg-[var(--q3-danger-light)] text-[var(--q3-neutral-default)] '
                                          : isSelected && !isAnswered
                                            ? 'bg-[var(--q3-accent-normal)] text-white'
                                            : 'bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)]'
                                    }`}
                                  >
                                    {isCorrect && isAnswered ? (
                                      <Check size={16} weight="bold" />
                                    ) : (
                                      String.fromCharCode(65 + index)
                                    )}
                                  </div>
                                </div>

                                <div className="flex flex-col w-full">
                                  <Latex>{option.text}</Latex>
                                </div>
                              </div>
                            </div>
                          </div>

                          {(isCorrect || (isWrong && isAnswered)) && (
                            <div className="absolute -top-3.5 right-4">
                              <span
                                className={`px-3 py-1 ${
                                  isCorrect ? 'text-[var(--q3-green-normal)]' : 'text-[var(--q3-danger-normal)]'
                                } text-sm font-medium flex items-center gap-1 bg-[var(--q3-surface-default)] rounded`}
                              >
                                {isCorrect ? (
                                  <>
                                    {isMarked ? 'Your Answer • Correct' : 'Correct'}
                                    <div className="rounded-full bg-[var(--q3-green-normal)] p-[2.4px]">
                                      <Check size={12} className="text-white" />
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    {isMarked ? 'Your Answer • Incorrect' : 'Incorrect'}
                                    <div className="rounded-full bg-[var(--q3-danger-normal)] p-[2.4px] text-white">
                                      <X size={12} weight="bold" />
                                    </div>
                                  </>
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {showSolution && (
                <div ref={solutionRef} className="mb-6">
                  <div className="text-[var(--q3-neutral-light)] text-sm font-medium mb-3 uppercase tracking-wide">
                    TEXT SOLUTION
                  </div>
                  <div className="text-[var(--q3-neutral-default)] text-sm leading-relaxed space-y-2">
                    <Latex>{currentQuestionData.solution?.text}</Latex>
                  </div>
                </div>
              )}
            </div>
          </div>

          {!isAnswered && (
            <div className="flex items-center justify-center gap-2 text-xs px-4 md:px-6 py-2 border-t border-[var(--q3-surface-dim)] bg-[var(--q3-surface-dim)] flex-shrink-0">
              <SparkleIcon size={12} className="text-[var(--q3-violet-normal)]" />
              <span className="text-[var(--q3-neutral-light)]">AI-generated Qs — may sometimes be inaccurate</span>
            </div>
          )}

          {isAnswered && !showSolution && (
            <div className="flex flex-col items-center px-4 md:px-6 py-4 flex-shrink-0">
              <button
                onClick={scrollToSolution}
                className="text-[var(--q3-neutral-light)] text-sm hover:text-[var(--q3-neutral-default)] transition-colors mb-2 flex items-center gap-1"
              >
                Click here to view solution
              </button>
              <div className="relative flex flex-col items-center">
                <div className="w-px h-6 border-l border-dashed border-[var(--q3-neutral-light)]"></div>
                <div className="w-0 h-0 border-l-4 border-r-4 border-t-6 border-transparent border-t-[var(--q3-neutral-light)]"></div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between p-4 md:p-6 border-t border-[var(--q3-surface-dim)] flex-shrink-0 gap-3">
            <button
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
              className="flex-1 px-4 md:px-6 py-2 rounded-full border border-[var(--q3-stroke-normal)] bg-[var(--q3-surface-default)] text-[var(--q3-neutral-light)] hover:text-[var(--q3-neutral-default)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer text-sm md:text-base"
            >
              Previous
            </button>

            {!isAnswered ? (
              <button
                ref={checkAnswerButtonRef}
                className="check-answer-button-target flex-1 px-6 md:px-8 py-2 bg-[var(--q3-accent-normal)] text-white rounded-full font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[var(--q3-accent-normal)]/90 transition-colors cursor-pointer text-sm md:text-base"
                onClick={handleCheckAnswer}
                disabled={isNumerical ? !numericalInput : selectedOption === null}
              >
                Check Answer
              </button>
            ) : (
              <button
                ref={viewSolutionButtonRef}
                className="view-solution-button-target flex-1 px-6 md:px-8 py-2 bg-[var(--q3-neutral-default)] text-[var(--q3-neutral-default_inverted)] rounded-full font-medium hover:bg-[var(--q3-neutral-default)]/90 transition-colors cursor-pointer text-sm md:text-base flex items-center justify-center gap-2"
                onClick={scrollToSolution}
              >
                View Solution
                <ArrowDown size={16} />
              </button>
            )}

            <button
              onClick={handleNext}
              disabled={currentQuestion === data.questions?.length - 1 && !checkIfAllQuestionsAttempted()}
              className="flex-1 px-4 md:px-6 py-2 rounded-full border border-[var(--q3-stroke-normal)] bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)] hover:text-[var(--q3-neutral-light)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium cursor-pointer text-sm md:text-base"
            >
              {currentQuestion < data.questions?.length - 1 ? 'Next' : 'Done'}
            </button>
          </div>
        </div>

        <ReportModal
          show={showReportModal}
          onClose={() => setShowReportModal(false)}
          onReport={handleReportQuestion}
          isLoading={isReporting}
          isReported={isReported}
          reportOptions={reportOptions}
          handleOnChange={handleReportOptionChange}
          commentsRef={commentsRef}
          reportMessage={reportMessage}
          DAY_LIMIT={DAY_LIMIT}
        />
      </div>
    </>
  );
};

export default QuestionAttempt;
